import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard } from "@project/admin/components/module-ui";
import { AdmissionDetailViewDialog } from "@project/admin/components/admission-detail-view-dialog";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@project/components/ui/alert-dialog";
import { TablePagination } from "@project/admin/components/table-pagination";
import {
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  Loader2,
  ClipboardList,
} from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { formatDate } from "@project/lib/date-format";
import { exportApiRowsToCsv } from "@project/lib/csv";
import { CsvExportButton } from "@project/admin/components/csv-export-button";
import { elderAdmissionService } from "@project/services/admin/elder-admission.service";
import { fetchAllListData } from "@project/services/admin/list-api-helpers";
import type { ApiPagination } from "@project/types/location";
import {
  formatAdmissionNumber,
  normalizeAdmissionStatus,
  type ElderAdmissionRecord,
  type ElderAdmissionStats,
  type ElderAdmissionStatus,
} from "@project/types/elder-admission";

type ApprovalTab = "All" | "Pending" | "Approved" | "Rejected";
type ActionKind = "approve" | "reject";

const TAB_STATUS: Record<Exclude<ApprovalTab, "All">, string> = {
  Pending: "pending",
  Approved: "approved",
  Rejected: "rejected",
};

const PAGE_SIZE = 20;

const EMPTY_STATS: ElderAdmissionStats = {
  total: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
};

function applyListResult(
  result: {
    data?: ElderAdmissionRecord[];
    pagination: ApiPagination;
    stats?: ElderAdmissionStats;
  },
  tab: ApprovalTab
) {
  const data =
    tab === "All" ? filterByTab(result.data ?? [], tab) : (result.data ?? []);
  return {
    data,
    pagination: result.pagination,
    stats: result.stats ?? EMPTY_STATS,
  };
}

function statusBadge(status: ElderAdmissionStatus) {
  const normalized = normalizeAdmissionStatus(status);
  const map = {
    pending: {
      label: "Pending",
      className: "bg-amber-500/15 text-amber-700 hover:bg-amber-500/15",
    },
    approved: {
      label: "Approved",
      className: "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15",
    },
    rejected: {
      label: "Rejected",
      className: "bg-rose-500/15 text-rose-700 hover:bg-rose-500/15",
    },
  } as const;
  const item = map[normalized];
  return <Badge className={item.className}>{item.label}</Badge>;
}

function filterByTab(
  rows: ElderAdmissionRecord[],
  tab: ApprovalTab
): ElderAdmissionRecord[] {
  if (tab === "All") {
    return rows;
  }
  const target = TAB_STATUS[tab];
  return rows.filter(row => normalizeAdmissionStatus(row.status) === target);
}

export default function AdmissionApprovalPage() {
  const [tab, setTab] = useState<ApprovalTab>("All");
  const [rows, setRows] = useState<ElderAdmissionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<ApiPagination>({
    count: 0,
    current_page: 1,
    total_pages: 1,
    next: null,
    previous: null,
  });
  const [stats, setStats] = useState<ElderAdmissionStats>(EMPTY_STATS);
  const [viewTarget, setViewTarget] = useState<ElderAdmissionRecord | null>(
    null
  );
  const [actionTarget, setActionTarget] = useState<{
    row: ElderAdmissionRecord;
    kind: ActionKind;
  } | null>(null);
  const [acting, setActing] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  useEffect(() => {
    let ignore = false;

    const run = async () => {
      setLoading(true);
      try {
        const result = await elderAdmissionService.listAdmissionsPage({
          page,
          page_size: PAGE_SIZE,
          status: tab === "All" ? undefined : TAB_STATUS[tab],
        });

        if (ignore) {
          return;
        }

        const next = applyListResult(result, tab);
        setRows(next.data);
        setPagination(next.pagination);
        setStats(next.stats);
        if (next.pagination.current_page !== page) {
          setPage(next.pagination.current_page);
        }
      } catch (error) {
        if (!ignore) {
          projectToast.error(
            getApiErrorMessage(error, "Failed to load admission applications.")
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    void run();
    return () => {
      ignore = true;
    };
  }, [page, tab]);

  const refreshAll = () => {
    void elderAdmissionService
      .listAdmissionsPage({
        page,
        page_size: PAGE_SIZE,
        status: tab === "All" ? undefined : TAB_STATUS[tab],
      })
      .then(result => {
        const next = applyListResult(result, tab);
        setRows(next.data);
        setPagination(next.pagination);
        setStats(next.stats);
      })
      .catch(error => {
        projectToast.error(
          getApiErrorMessage(error, "Failed to load admission applications.")
        );
      });
  };

  const handleTabChange = (next: ApprovalTab) => {
    if (next === tab) {
      return;
    }
    setTab(next);
    setPage(1);
  };

  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const exportRows = await fetchAllListData(
        query => elderAdmissionService.listAdmissionsPage(query),
        { status: tab === "All" ? undefined : TAB_STATUS[tab] }
      );
      if (!exportRows.length) {
        projectToast.error("Nothing to export");
        return;
      }
      exportApiRowsToCsv(
        `admission-approvals-${new Date().toISOString().slice(0, 10)}`,
        exportRows as Record<string, unknown>[]
      );
      projectToast.success(`Exported ${exportRows.length} applications`);
    } catch (error) {
      projectToast.error(getApiErrorMessage(error, "Failed to export CSV"));
    } finally {
      setExportingCsv(false);
    }
  };

  const confirmAction = async () => {
    if (!actionTarget) {
      return;
    }
    setActing(true);
    try {
      const { row, kind } = actionTarget;
      if (kind === "approve") {
        await elderAdmissionService.approve(row.id);
        projectToast.success(
          `${formatAdmissionNumber(row)} approved successfully.`
        );
      } else {
        await elderAdmissionService.reject(row.id);
        projectToast.success(`${formatAdmissionNumber(row)} rejected.`);
      }
      setActionTarget(null);
      refreshAll();
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, "Could not update application status.")
      );
    } finally {
      setActing(false);
    }
  };

  const tabCounts: Record<ApprovalTab, number> = {
    All: stats.total,
    Pending: stats.pending,
    Approved: stats.approved,
    Rejected: stats.rejected,
  };

  const emptyMessage = useMemo(() => {
    if (tab === "All") {
      return "No admission applications found.";
    }
    if (tab === "Pending") {
      return "No pending applications to review.";
    }
    if (tab === "Approved") {
      return "No approved applications yet.";
    }
    return "No rejected applications.";
  }, [tab]);

  const canApproveReject = (row: ElderAdmissionRecord) =>
    normalizeAdmissionStatus(row.status) === "pending";

  return (
    <AdminShell
      title="Admission Approval"
      subtitle="Review and approve admission applications"
    >
      <AdmissionDetailViewDialog
        open={!!viewTarget}
        onOpenChange={open => {
          if (!open) {
            setViewTarget(null);
          }
        }}
        admissionId={viewTarget?.id ?? null}
        preview={viewTarget}
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total Applications"
          value={stats.total}
          icon={<ClipboardList className="h-5 w-5" strokeWidth={2.25} />}
          accent="from-sky-600 to-sky-500"
        />
        <StatCard
          label="Pending Review"
          value={stats.pending}
          icon={<Clock className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
        />
        <StatCard
          label="Approved"
          value={stats.approved}
          icon={<CheckCircle2 className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
        />
        <StatCard
          label="Rejected"
          value={stats.rejected}
          icon={<XCircle className="h-5 w-5" />}
          accent="from-rose-600 to-rose-500"
        />
      </div>

      <div className="mb-4 mt-6 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1">
          {(["All", "Pending", "Approved", "Rejected"] as const).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => handleTabChange(s)}
              className={`rounded-full border px-3 py-1 text-xs transition ${
                tab === s
                  ? "border-primary bg-primary text-primary-foreground"
                  : "hover:border-primary/40 border-border bg-card"
              }`}
            >
              {s}
              <span className="ml-1 opacity-80">({tabCounts[s]})</span>
            </button>
          ))}
        </div>
        <CsvExportButton
          onExport={exportCsv}
          exporting={exportingCsv}
          className="ml-auto"
        />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow>
              <TableHead>Application #</TableHead>
              <TableHead>Elder Name</TableHead>
              <TableHead>Applicant</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-10 text-center text-muted-foreground"
                >
                  <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Loading applications…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-10 text-center text-sm text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-xs">
                    {formatAdmissionNumber(row)}
                  </TableCell>
                  <TableCell className="font-medium">
                    {row.elder_name}
                  </TableCell>
                  <TableCell>{row.applicant_name}</TableCell>
                  <TableCell>{formatDate(row.created_at) || "—"}</TableCell>
                  <TableCell>{statusBadge(row.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={() => setViewTarget(row)}
                        aria-label={`View ${formatAdmissionNumber(row)}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {canApproveReject(row) && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-emerald-700"
                            onClick={() =>
                              setActionTarget({ row, kind: "approve" })
                            }
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-rose-700"
                            onClick={() =>
                              setActionTarget({ row, kind: "reject" })
                            }
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={pagination.count}
        totalPages={pagination.total_pages}
        loading={loading}
        onPageChange={setPage}
      />

      <AlertDialog
        open={!!actionTarget}
        onOpenChange={open => {
          if (!open && !acting) {
            setActionTarget(null);
          }
        }}
      >
        <AlertDialogContent className="max-w-md rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionTarget?.kind === "approve"
                ? "Approve application?"
                : "Reject application?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionTarget?.kind === "approve"
                ? "This will mark the admission application as approved. The applicant may be contacted for next steps."
                : "This will mark the admission application as rejected. This action should be used after review."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {actionTarget && (
            <div className="bg-muted/40 rounded-xl border border-border px-4 py-3 text-sm">
              <p className="font-mono text-xs text-muted-foreground">
                {formatAdmissionNumber(actionTarget.row)}
              </p>
              <p className="mt-1 font-medium text-foreground">
                {actionTarget.row.elder_name}
              </p>
              <p className="text-muted-foreground">
                Applicant: {actionTarget.row.applicant_name}
              </p>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={acting} className="rounded-xl">
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              disabled={acting}
              className={`rounded-xl ${
                actionTarget?.kind === "approve"
                  ? "bg-emerald-600 text-white hover:bg-emerald-600/90"
                  : "hover:bg-destructive/90 bg-destructive text-destructive-foreground"
              }`}
              onClick={() => void confirmAction()}
            >
              {acting
                ? "Processing…"
                : actionTarget?.kind === "approve"
                  ? "Approve"
                  : "Reject"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
